import React, { useCallback, useEffect, useState } from "react";
import "@sendbird/uikit-react/dist/index.css";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import {
  CHAT_DETAIL_ENDPOINT,
  CHAT_DOMAIN,
  CHAT_READ_MESSAGE_ENDPOINT,
  chatApi,
} from "api/chatApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GroupChannel } from "@sendbird/uikit-react/GroupChannel";
import { userWebChatAtom } from "recoil/userWebChatAtom";
import { MessageList } from "@sendbird/uikit-react/GroupChannel/components/MessageList";
import { Message } from "@sendbird/uikit-react/GroupChannel/components/Message";
import {
  Box,
  Button,
  GlobalStyles,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { RenderCustomSeparatorProps } from "@sendbird/uikit-react/types/types";
import ModalDialog from "@mui/joy/ModalDialog";
import Modal from "@mui/joy/Modal";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useNavigate } from "react-router-dom";
import MessageContent, {
  MessageBody,
} from "@sendbird/uikit-react/ui/MessageContent";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";

interface CustomMessageInputProps {
  channelUrl?: string;
}

function CustomMessageInput({ channelUrl }: CustomMessageInputProps) {
  const [message, setMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const userWebChatState = useRecoilValue(userWebChatAtom);
  const sendbirdState = useSendbirdStateContext();
  const { scrollToBottom } = useGroupChannelContext();

  const navigate = useNavigate();

  const [isSendAvailable, setIsSendAvailable] = useState(false);

  useEffect(() => {
    if (message.trim().length > 0) {
      setIsSendAvailable(true);
    } else {
      setIsSendAvailable(false);
    }
  }, [message]);

  const sendMessage = () => {
    if (!userWebChatState?.isSendAvailable) {
      setOpenModal(true);
      return;
    }

    const sb = sendbirdState.stores.sdkStore.sdk;
    if (!sb || !channelUrl || !message.trim()) return;

    sb.groupChannel
      .getChannel(channelUrl)
      .then((channel) => {
        const params = {
          // UserMessageCreateParams can be imported from @sendbird/chat/message.
          message: message,
        };
        channel.sendUserMessage(params).onSucceeded((sentMessage) => {
          setMessage("");
          scrollToBottom(true);
        });
      })
      .catch((error) => {
        alert("다시 시도해 주세요");
      });
  };

  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
        >
          <Typography id="nested-modal-title" level="h2" textAlign={"start"}>
            결제하고 이용해주세요
          </Typography>
          <Typography
            id="nested-modal-description"
            textAlign={"start"}
            textColor="text.tertiary"
          >
            다시 결제하고 프로그램을 이어가세요
          </Typography>
          <Button
            variant="solid"
            size="lg"
            sx={{ mt: 2 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenModal(false);
              navigate("/payment", { replace: true });
            }}
          >
            계속 이용하기
          </Button>
        </ModalDialog>
      </Modal>

      <Box
        sx={{
          width: "100%",
          mx: "auto",
          px: "16px",
          "@media (min-width: 1000px)": {
            px: "200px !important",
          },
          "@media (min-width: 1400px)": {
            px: "400px !important",
          },
          "@media (min-width: 1700px)": {
            px: "33% !important",
          },
          py: "8px",
          backgroundColor: "background.level1",
        }}
      >
        <Stack direction={"row"} alignItems="end" spacing={1}>
          <Textarea
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            onKeyPress={(e) => {
              if (
                isSendAvailable &&
                message.trim().length > 0 &&
                !e.shiftKey &&
                (e.code === "Enter" ||
                  e.code === "NumpadEnter" ||
                  e.key === "Enter" ||
                  e.key === "NumpadEnter")
              ) {
                e.preventDefault();
                sendMessage();
              }
            }}
            sx={{
              flex: 1,
              borderColor: "background.level3",
              backgroundColor: "background.level3",
              "--joy-focus-thickness": "0px",
            }}
          />
          <Button
            size="sm"
            disabled={message.trim().length === 0}
            sx={{ px: "10px" }}
            onClick={sendMessage}
          >
            <span className="material-symbols-rounded">send</span>
          </Button>
        </Stack>
      </Box>
    </>
  );
}

export default function ChatPatientContent() {
  const user = useRecoilValue(userAtom);
  const [userWebChatState, setUserWebChatState] =
    useRecoilState(userWebChatAtom);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  const getDetailQueryFn = useCallback(() => chatApi.getDetail(), []);
  const queryClient = useQueryClient();

  const { refetch, isLoading } = useQuery(
    [CHAT_DOMAIN, CHAT_DETAIL_ENDPOINT, user?.userId],
    getDetailQueryFn,
    {
      enabled: !!user?.userId && user.role === "patient",
      refetchIntervalInBackground: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setUserWebChatState(data.result);
        }
      },
    }
  );

  const messageReadQueryFn = useCallback(() => chatApi.read(), [user]);

  const { refetch: readMessage } = useQuery(
    [CHAT_DOMAIN, CHAT_READ_MESSAGE_ENDPOINT],
    messageReadQueryFn,
    {
      enabled: !!user?.userId,
      onSuccess: () => {
        queryClient.invalidateQueries([
          CHAT_DOMAIN,
          CHAT_DETAIL_ENDPOINT,
          user?.userId,
        ]);
      },
    }
  );

  useEffect(() => {
    if (
      userWebChatState !== undefined &&
      !userWebChatState?.chatConnection?.channelUrl &&
      !isLoading
    ) {
      setOpenModal(true);
    }
  }, [userWebChatState, isLoading]);

  if (!userWebChatState?.chatConnection?.channelUrl) {
    return (
      <>
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <ModalDialog
            aria-labelledby="nested-modal-title"
            aria-describedby="nested-modal-description"
          >
            <Typography id="nested-modal-title" level="h2" textAlign={"center"}>
              프로그램을 시작하고 이용해주세요
            </Typography>
            <Typography
              id="nested-modal-description"
              textAlign={"center"}
              textColor="text.tertiary"
            >
              코치 선생님과 프로그램을 시작하세요
            </Typography>
            <Button
              variant="solid"
              size="lg"
              sx={{ mt: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(false);
                navigate("/payment", { replace: true });
              }}
            >
              프로그램 시작하기
            </Button>
          </ModalDialog>
        </Modal>
      </>
    );
  }

  return (
    <div style={{ height: "100%", width: "100%", minWidth: "520px" }}>
      <GlobalStyles
        styles={{
          ".sendbird-conversation, .sendbird-chat-header, .sendbird-message-input-wrapper":
            {
              border: "none !important",
            },
          ".sendbird-message-input--textarea": {
            border: "1.4px solid #BCC7D0 !important",
            borderRadius: "12px !important",
            boxShadow: "none !important",
            "--joy-focus-thickness": "0px !important",
          },

          ".sendbird-message-content__right__created-at": {
            display: "inline-flex !important",
            opacity: "1 !important",
            visibility: "visible !important",
          },
          //
          // ".mouse-hover": {
          //   backgroundColor: "transparent !important",
          // },

          "@media (min-width: 1000px)": {
            ".sendbird-conversation__messages-padding, .sendbird-message-input-wrapper":
              {
                paddingLeft: "200px !important",
                paddingRight: "200px !important",
              },
          },

          "@media (min-width: 1400px)": {
            ".sendbird-conversation__messages-padding, .sendbird-message-input-wrapper":
              {
                paddingLeft: "400px !important",
                paddingRight: "400px !important",
              },
          },
          "@media (min-width: 1700px)": {
            ".sendbird-conversation__messages-padding, .sendbird-message-input-wrapper":
              {
                paddingLeft: "33% !important",
                paddingRight: "33% !important",
              },
          },

          ".sendbird-conversation__scroll-bottom-button": {
            display: "none",
          },
        }}
      />
      <GroupChannel
        channelUrl={userWebChatState?.chatConnection?.channelUrl}
        renderChannelHeader={() => <></>}
        renderMessageContent={(props) => (
          <MessageContent
            {...props}
            renderEmojiMenu={() => undefined}
            renderMessageMenu={() => undefined}
            renderEmojiReactions={() => undefined}
            renderMessageBody={(props) => (
              <MessageBody {...props} mouseHover={false} />
            )}
          />
        )}
        isReactionEnabled={false}
        isMultipleFilesMessageEnabled={false}
        disableUserProfile={true}
        replyType={"NONE"}
        renderCustomSeparator={(props: RenderCustomSeparatorProps) => {
          const date = new Date(props.message.createdAt);
          const formattedDate = date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });

          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                my: 2,
              }}
            >
              <Typography
                level={"body-xs"}
                fontSize={10}
                style={{ color: "#ffffff", textAlign: "center" }}
                sx={{
                  backgroundColor: "#7c7c7c99",
                  borderRadius: "16px",
                  px: 2,
                  py: 0.5,
                }}
              >
                {formattedDate}
              </Typography>
            </Box>
          );
        }}
        renderMessageList={(props) => <MessageList {...props} />}
        renderMessageInput={() => (
          <CustomMessageInput
            channelUrl={userWebChatState?.chatConnection?.channelUrl}
          />
        )}
        renderMessage={(props) => <Message {...props} />}
      />
    </div>
  );
}